import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, X } from "lucide-react";
import { BRAND, ANNOUNCEMENTS, PRODUCT } from "../mock";
import { useCart } from "../context/CartContext";

const AnnouncementBar = () => (
  <div className="bg-[#1A1A1A] text-[#F4EFE8] text-[11px] tracking-[0.15em] overflow-hidden">
    <div className="flex whitespace-nowrap animate-marquee">
      {[...ANNOUNCEMENTS, ...ANNOUNCEMENTS, ...ANNOUNCEMENTS, ...ANNOUNCEMENTS].map((a, i) => (
        <span key={i} className="px-8 py-2.5 inline-flex items-center">
          {a}
        </span>
      ))}
    </div>
  </div>
);

const Header = () => {
  const { count, setIsOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: "Home", to: "/" },
    { label: "Shop", to: `/products/${PRODUCT.handle}` },
    { label: "Results", to: `/products/${PRODUCT.handle}#science` },
    { label: "Reviews", to: `/products/${PRODUCT.handle}#reviews` },
    { label: "Blogs", to: "/blog" },
    { label: "Contact", to: "/contact" },
    { label: "FAQ", to: "/#faq" },
  ];

  return (
    <header className="sticky top-0 z-50">
      <AnnouncementBar />
      <div
        className={`transition-all duration-300 ${
          scrolled ? "bg-[#F4EFE8]/80 backdrop-blur-md shadow-[0_1px_0_rgba(0,0,0,0.06)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between h-[68px] md:h-[80px]">
            {/* Logo (left) */}
            <Link to="/" className="flex items-center gap-2 md:gap-3">
              <img
                src="/flowlux-mask.png"
                alt=""
                aria-hidden="true"
                className="h-9 md:h-11 w-auto object-contain flex-shrink-0"
              />
              <span className="font-serif-display font-bold text-[22px] md:text-[30px] leading-none tracking-[0.24em] text-[#1A1A1A] pl-[0.24em]">
                FLOWLUX
              </span>
            </Link>

            {/* Right group: nav + icons (desktop) / cart + menu (mobile) */}
            <div className="flex items-center gap-6 lg:gap-8">
              <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-[12px] tracking-[0.18em] uppercase">
                {links.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    className="relative text-[#1A1A1A]/80 hover:text-[#1A1A1A] transition-colors after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#1A1A1A] hover:after:w-full after:transition-all after:duration-300"
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-4 md:gap-5">
                <button className="hidden md:block hover:opacity-60 transition" aria-label="Search">
                  <Search size={19} strokeWidth={1.4} />
                </button>
                <button
                  onClick={() => setIsOpen(true)}
                  className="relative hover:opacity-60 transition"
                  aria-label="Cart"
                >
                  <ShoppingBag size={20} strokeWidth={1.4} />
                  {count > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#1A1A1A] text-[#F4EFE8] text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center">
                      {count}
                    </span>
                  )}
                </button>
                <button
                  className="md:hidden p-1"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Menu"
                >
                  <Menu size={22} strokeWidth={1.4} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[78%] max-w-xs bg-[#F4EFE8] p-6 animate-rise">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-2">
                <img src="/flowlux-mask.png" alt="" aria-hidden="true" className="h-10 w-auto object-contain" />
                <span className="font-serif-display font-bold text-[20px] leading-none tracking-[0.24em] pl-[0.24em]">FLOWLUX</span>
              </div>
              <button onClick={() => setMobileOpen(false)}>
                <X size={22} strokeWidth={1.4} />
              </button>
            </div>
            <nav className="flex flex-col gap-5 text-sm tracking-[0.18em] uppercase">
              {links.map((l) => (
                <button
                  key={l.label}
                  className="text-left"
                  onClick={() => {
                    setMobileOpen(false);
                    navigate(l.to);
                  }}
                >
                  {l.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
